import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { format } from "date-fns"
import { de } from "date-fns/locale/de"
import { useState } from "react"
import type { ComponentProps } from "react"
import { cn } from "cn"

import { Calendar } from "../calendar/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"

/**
 * Figma „Input Date Picker" (node 9752:29332). Auf Mobile schaltet `Popover`
 * bereits automatisch auf `Drawer` um (siehe popover.tsx) — das deckt die
 * Figma-Mobile-Section „Drawer Date Picker" ab, ohne eigene Verzweigung hier.
 *
 * `Date Navigator` / `Date Button` (eigene Component-Sets im selben Figma-Frame)
 * sind eine separate Paginierungs-Komponente (z. B. für Listen-Header) und
 * bewusst nicht Teil dieser Komponente — mit dem Nutzer nicht als Umfang
 * bestätigt.
 */
export type DatePickerProps = Omit<
  ComponentProps<"button">,
  "onChange" | "value"
> & {
  /** Figma: Label über dem Feld (`Labels`-Slot), Default „Datum". */
  label?: string
  placeholder?: string
  value?: Date
  onChange?: (date: Date | undefined) => void
  disabled?: boolean
}

function DatePicker({
  className,
  label = "Datum",
  placeholder = "TT.MM.JJJJ",
  value,
  onChange,
  disabled,
  id,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const inputId = id ?? "date-picker-input"

  return (
    <div
      className="flex w-full flex-col items-start gap-1"
      data-slot="date-picker"
    >
      <label
        htmlFor={inputId}
        className="text-xs leading-4 font-bold text-labels-primary"
      >
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={inputId}
            disabled={disabled}
            data-slot="date-picker-trigger"
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary py-3 pr-3 pl-4 text-sm disabled:cursor-not-allowed disabled:opacity-60",
              value ? "text-labels-primary" : "text-labels-secondary",
              className
            )}
            {...props}
          >
            {value ? format(value, "dd.MM.yyyy", { locale: de }) : placeholder}
            <FontAwesomeIcon
              icon={faCalendar}
              className="size-[18px] shrink-0 text-labels-primary"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-full border-none bg-transparent p-0 shadow-none md:w-fit">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            className="w-full md:w-fit"
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
DatePicker.displayName = "DatePicker"

export { DatePicker }
